import Dialog from '@mui/material/Dialog';
import React from 'react';
import type { AdminLegalProfileConflict } from '../../store/useSuperAdminStore';
import type { AdminLegalProfile, AdminLegalProfileUpdate, ETagged } from '../../legalTypes';
import { AdminButton } from '../components';

const editableProfile = (profile: AdminLegalProfile): AdminLegalProfileUpdate => ({
  legalName: profile.legalName,
  registrationNumber: profile.registrationNumber,
  taxId: profile.taxId,
  addressLine1: profile.addressLine1,
  addressLine2: profile.addressLine2,
  city: profile.city,
  postalCode: profile.postalCode,
  country: profile.country,
  locale: profile.locale,
  jurisdiction: profile.jurisdiction,
  signatoryName: profile.signatoryName,
  signatoryTitle: profile.signatoryTitle,
  signatoryEmail: profile.signatoryEmail,
});

const validateProfile = (profile: AdminLegalProfileUpdate): string[] => {
  const required: ReadonlyArray<[keyof AdminLegalProfileUpdate, string]> = [
    ['legalName', 'Verified legal name'],
    ['registrationNumber', 'Registration number'],
    ['taxId', 'Tax ID'],
    ['addressLine1', 'Address line 1'],
    ['city', 'City'],
    ['postalCode', 'Postal code'],
    ['country', 'Country'],
    ['locale', 'Language / locale'],
    ['jurisdiction', 'Jurisdiction'],
    ['signatoryName', 'Authorized signatory name'],
    ['signatoryTitle', 'Authorized signatory title'],
    ['signatoryEmail', 'Authorized signatory email'],
  ];
  return required
    .filter(([key]) => !profile[key]?.trim())
    .map(([, label]) => `${label} is required.`);
};

interface ProfileFieldsProps {
  value: AdminLegalProfileUpdate;
  onChange: (key: keyof AdminLegalProfileUpdate, value: string) => void;
}

const ProfileFields: React.FC<ProfileFieldsProps> = ({ value, onChange }) => (
  <div className="admin-legal-profile-fields">
    <fieldset className="admin-legal-fieldset">
      <legend>Verified legal identity</legend>
      <label>Verified legal name<input autoFocus value={value.legalName} onChange={(event) => onChange('legalName', event.target.value)} /></label>
      <div className="admin-legal-form-grid">
        <label>Registration number<input value={value.registrationNumber} onChange={(event) => onChange('registrationNumber', event.target.value)} /></label>
        <label>Tax ID<input value={value.taxId} onChange={(event) => onChange('taxId', event.target.value)} /></label>
      </div>
    </fieldset>
    <fieldset className="admin-legal-fieldset">
      <legend>Registered address</legend>
      <label>Address line 1<input value={value.addressLine1} onChange={(event) => onChange('addressLine1', event.target.value)} /></label>
      <label>Address line 2<input value={value.addressLine2 ?? ''} onChange={(event) => onChange('addressLine2', event.target.value)} /></label>
      <div className="admin-legal-form-grid">
        <label>City<input value={value.city} onChange={(event) => onChange('city', event.target.value)} /></label>
        <label>Postal code<input value={value.postalCode} onChange={(event) => onChange('postalCode', event.target.value)} /></label>
        <label>Country<input value={value.country} onChange={(event) => onChange('country', event.target.value)} /></label>
        <label>Jurisdiction<input value={value.jurisdiction} onChange={(event) => onChange('jurisdiction', event.target.value)} /></label>
        <label>Language / locale<input value={value.locale} onChange={(event) => onChange('locale', event.target.value)} /></label>
      </div>
    </fieldset>
    <fieldset className="admin-legal-fieldset">
      <legend>Authorized signatory</legend>
      <div className="admin-legal-form-grid">
        <label>Authorized signatory name<input value={value.signatoryName} onChange={(event) => onChange('signatoryName', event.target.value)} /></label>
        <label>Authorized signatory title<input value={value.signatoryTitle} onChange={(event) => onChange('signatoryTitle', event.target.value)} /></label>
      </div>
      <label>Authorized signatory email<input type="email" value={value.signatoryEmail} onChange={(event) => onChange('signatoryEmail', event.target.value)} /></label>
    </fieldset>
  </div>
);

interface ProfileEditDialogProps {
  open: boolean;
  profile: AdminLegalProfile;
  saving: boolean;
  onSave: (input: AdminLegalProfileUpdate) => Promise<ETagged<AdminLegalProfile>>;
  onClose: () => void;
}

const ProfileEditDialog: React.FC<ProfileEditDialogProps> = ({
  open,
  profile,
  saving,
  onSave,
  onClose,
}) => {
  const [draft, setDraft] = React.useState(() => editableProfile(profile));
  const [errors, setErrors] = React.useState<string[]>([]);
  React.useEffect(() => setDraft(editableProfile(profile)), [profile]);
  const save = async () => {
    const validation = validateProfile(draft);
    setErrors(validation);
    if (validation.length > 0) {
      return;
    }
    try {
      await onSave({ ...draft, addressLine2: draft.addressLine2 || null });
      onClose();
    } catch {
      return;
    }
  };
  return (
    <Dialog open={open} aria-labelledby="legal-profile-editor-title" maxWidth="md" fullWidth className="pipeex-admin-portal admin-legal-dialog" onClose={() => !saving && onClose()}>
      <form className="admin-legal-dialog__body" onSubmit={(event) => { event.preventDefault(); void save(); }}>
        <header><span className="admin-legal-eyebrow">Optimistic concurrency · version <span data-visual-mask>{profile.version}</span></span><h2 id="legal-profile-editor-title">Edit legal profile</h2><p>Changes affect future package snapshots only. Existing generated versions remain immutable.</p></header>
        {errors.length > 0 && <div role="alert" aria-label="Legal profile validation summary" className="admin-legal-callout admin-legal-callout--danger"><ul>{errors.map((error) => <li key={error}>{error}</li>)}</ul></div>}
        <ProfileFields value={draft} onChange={(key, fieldValue) => setDraft((current) => ({ ...current, [key]: fieldValue }))} />
        <footer className="admin-legal-dialog__actions"><AdminButton variant="ghost" disabled={saving} onClick={onClose}>Cancel profile edit</AdminButton><AdminButton type="submit" variant="primary" disabled={saving}>Save legal profile</AdminButton></footer>
      </form>
    </Dialog>
  );
};

interface LegalProfileEditorProps {
  profile: ETagged<AdminLegalProfile>;
  conflict: AdminLegalProfileConflict | null;
  saving: boolean;
  onSave: (input: AdminLegalProfileUpdate) => Promise<ETagged<AdminLegalProfile>>;
  onRefresh: () => Promise<void>;
}

const LegalProfileEditor: React.FC<LegalProfileEditorProps> = ({
  profile,
  conflict,
  saving,
  onSave,
  onRefresh,
}) => {
  const [editing, setEditing] = React.useState(false);
  return (
    <section className="admin-legal-card" aria-labelledby="legal-profile-title">
      <div className="admin-legal-card__heading"><div><span className="admin-legal-eyebrow">Verified customer identity</span><h3 id="legal-profile-title">Legal profile</h3></div><AdminButton onClick={() => setEditing(true)}>Edit legal profile</AdminButton></div>
      <dl className="admin-legal-detail-grid">
        <div><dt>Legal name</dt><dd>{profile.data.legalName}</dd></div>
        <div><dt>Registration</dt><dd data-visual-mask>{profile.data.registrationNumber}</dd></div>
        <div><dt>Tax ID</dt><dd data-visual-mask>{profile.data.taxId}</dd></div>
        <div><dt>Jurisdiction</dt><dd>{profile.data.jurisdiction}</dd></div>
        <div><dt>Locale</dt><dd>{profile.data.locale}</dd></div>
        <div><dt>Authorized signatory</dt><dd data-visual-mask>{profile.data.signatoryName}</dd></div>
      </dl>
      {conflict && <div role="alert" aria-label="Legal profile conflict" className="admin-legal-callout admin-legal-callout--danger"><strong>{conflict.error.message}</strong><p>Your version: {conflict.local.data.version}. Server version: {conflict.current.version}.</p><AdminButton onClick={() => void onRefresh()}>Refresh legal profile</AdminButton></div>}
      <ProfileEditDialog open={editing} profile={profile.data} saving={saving} onSave={onSave} onClose={() => setEditing(false)} />
    </section>
  );
};

export default LegalProfileEditor;
